import { brandNumber } from "@voltalis/frontend-utils";
import { onBeforeUnmount, Ref, ref, watch } from "vue";
import { computed } from "vue";
import { onBeforeRouteLeave, onBeforeRouteUpdate } from "vue-router";

import { useCreateSiteProgramMutation } from "@/composables/kili/internal/mutations/use-create-site-program-mutation";
import { useUpdateSiteProgramMutation } from "@/composables/kili/internal/mutations/use-update-site-program-mutation";
import { useUpdateSiteProgramSubprogramsMutation } from "@/composables/kili/internal/mutations/use-update-site-program-subprograms-mutation";
import { useGetSiteDetailsQuery } from "@/composables/kili/sites/queries/use-get-site-details-query";
import { useGetSiteInstalledApplianceCategoriesQuery } from "@/composables/kili/sites/queries/use-get-site-installed-appliance-categories-query";
import { useGetSiteProgramDetailsQuery } from "@/composables/kili/sites/queries/use-get-site-program-details-query";
import { useToaster } from "@/composables/layout/use-toaster";
import { useProgramEditor } from "@/composables/programming/use-program-editor";
import { useBrandedNumberRouteParams } from "@/composables/use-route-params/use-hospitality-number-route-param";
import { toastKiliError } from "@/data/api/kili/common/toast-kili-error";
import {
  DEFAULT_BLACKLISTED_PROGRAMS_APPLIANCE_CATEGORIES,
  DEFAULT_DISPLAYED_APPLIANCE_CATEGORIES,
} from "@/data/api/kili/programs/program.type";
import { ProgramId, V4HSiteId } from "@/data/common/ids";
import { defaultProgramColorName, ProgramDetails } from "@/domains/v4b/program/program.types";
import { useTypedIntl } from "@/locales/typed-intl";
import router from "@/router/router";
import { ERouteName } from "@/router/router.type";
import { EMixpanelEvent } from "@/services/mixpanel/mixpanel-events.type";
import { MixpanelService } from "@/services/mixpanel/mixpanel-service";
import { createDefaultSubPrograms } from "@/utils/programs/program-presets";
import { getProgramApplianceCategories } from "@/utils/programs/program-utils";

import { TCustomBreadcrumbRouteFormatter } from "@/components/layout/base-layout-breadcrumb.vue";

export function useProgrammingDetailsController(creationMode: Ref<boolean>) {
  const isEditing = ref<boolean>(creationMode.value);
  const showNameError = ref<boolean>(false);
  const isUnsavedChangesDialogVisible = ref<boolean>(false);

  let allowDirtyNavigation = false;
  let viewGeneration = 0;
  let pendingExitAction: (() => Promise<void> | void) | undefined;
  let pendingNavigationResolution: ((canNavigate: boolean) => void) | undefined;

  const { brandedNumberRouteParam: siteId } = useBrandedNumberRouteParams<V4HSiteId>("siteId");
  const { brandedNumberRouteParam: programId } = useBrandedNumberRouteParams<ProgramId>("programId", {
    optional: () => creationMode.value,
  });

  const { t, k } = useTypedIntl();
  const toast = useToaster();

  const { data: siteDetails, isPending: isSiteDetailsPending } = useGetSiteDetailsQuery(siteId);

  const {
    data: fetchedProgramDetails,
    isError: isProgramDetailsError,
    isPending: isProgramDetailsPending,
    refetch: refetchProgramDetails,
  } = useGetSiteProgramDetailsQuery(
    siteId,
    programId,
    DEFAULT_DISPLAYED_APPLIANCE_CATEGORIES,
    DEFAULT_BLACKLISTED_PROGRAMS_APPLIANCE_CATEGORIES,
  );

  const {
    data: installedApplianceCategories,
    isError: isInstalledApplianceCategoriesError,
    isPending: isInstalledApplianceCategoriesPending,
    isSuccess: isInstalledApplianceCategoriesSuccess,
    refetch: refetchInstalledApplianceCategories,
  } = useGetSiteInstalledApplianceCategoriesQuery(siteId);

  const { mutateAsync: updateSubprograms, isPending: isEditingProgram } = useUpdateSiteProgramSubprogramsMutation();
  const { mutateAsync: updateSiteProgram, isPending: isRenamingProgram } = useUpdateSiteProgramMutation();
  const { mutateAsync: createSiteProgram, isPending: isCreatingProgram } = useCreateSiteProgramMutation();

  const isSavingProgram = computed(() => isEditingProgram.value || isRenamingProgram.value);
  const isProgramMutationPending = computed(() => isSavingProgram.value || isCreatingProgram.value);

  const creationApplianceCategories = computed(() =>
    getProgramApplianceCategories(
      DEFAULT_DISPLAYED_APPLIANCE_CATEGORIES,
      installedApplianceCategories.value,
      DEFAULT_BLACKLISTED_PROGRAMS_APPLIANCE_CATEGORIES,
    ),
  );

  const programDetails = computed<ProgramDetails | undefined>(() => {
    if (creationMode.value) {
      if (!isInstalledApplianceCategoriesSuccess.value) return;

      return {
        id: brandNumber(0),
        name: "",
        siteId: siteId.value,
        subprograms: createDefaultSubPrograms(creationApplianceCategories.value),
        isSiteDefault: false,
        frontDisplayColor: defaultProgramColorName,
      } satisfies ProgramDetails;
    }

    return fetchedProgramDetails.value;
  });

  const isProgramSiteDefault = computed<boolean>(() => {
    return programDetails.value?.isSiteDefault ?? false;
  });

  const {
    name: programName,
    programColor,
    subPrograms,
    activeApplianceCategory,
    validatedApplianceCategories,
    displayedApplianceCategories,
    isInitialized,
    isNameValid,
    isConfigurationComplete,
    isDirty,
    canSave,
    activeQuickConfigurations,
    validateActiveApplianceCategoryAndSelectNext,
    toggleQuickConfiguration,
    handleManualProgramChange,
    save,
    discardChanges,
  } = useProgramEditor({
    programDetails,
    installedApplianceCategories,
    defaultApplianceCategories: DEFAULT_DISPLAYED_APPLIANCE_CATEGORIES,
    blackListedApplianceCategories: DEFAULT_BLACKLISTED_PROGRAMS_APPLIANCE_CATEGORIES,
    validateInitialSubPrograms: () => !creationMode.value,
  });

  const breadcrumbFormatter = computed<TCustomBreadcrumbRouteFormatter>(() => ({
    [ERouteName.PROGRAMMING_LIST]: () =>
      siteDetails.value?.name ?? t(k.components.layout.breadcrumb["programming-list"]),
    [ERouteName.PROGRAMMING_LIST_NEW]: () =>
      siteDetails.value?.name ?? t(k.components.layout.breadcrumb["new-programming-list"]),
    [ERouteName.PROGRAMMING_CREATION]: () => t(k.control.programming.createProgram),
    [ERouteName.PROGRAMMING_CREATION_NEW]: () => t(k.control.programming.createProgram),
    [ERouteName.PROGRAMMING_DETAILS]: () => programName.value,
    [ERouteName.PROGRAMMING_DETAILS_NEW]: () => programName.value,
  }));

  watch([siteId, programId, creationMode], () => {
    viewGeneration += 1;

    discardChanges();
    isEditing.value = !!creationMode.value;
    showNameError.value = false;
  });

  watch(isUnsavedChangesDialogVisible, (visible) => {
    if (visible) return;

    pendingExitAction = undefined;
    const resolveNavigation = pendingNavigationResolution;
    pendingNavigationResolution = undefined;
    resolveNavigation?.(false);
  });

  const isLastApplianceCategory = computed(
    () => displayedApplianceCategories.value.at(-1) === activeApplianceCategory.value,
  );

  const primaryActionLabel = computed(() =>
    !creationMode.value || isLastApplianceCategory.value
      ? t(k.control.programming.details.appliances.validateProgram)
      : t(k.control.programming.details.appliances.configureNext),
  );

  const isPrimaryActionDisabled = computed(() => {
    if (!creationMode.value) return isSavingProgram.value || !canSave.value;

    return (
      isCreatingProgram.value || (isLastApplianceCategory.value && isConfigurationComplete.value && !canSave.value)
    );
  });

  const isPrimaryActionLoading = computed(() => (creationMode.value ? isCreatingProgram.value : isSavingProgram.value));

  const isProgramLoadingError = computed(() =>
    creationMode.value ? isInstalledApplianceCategoriesError.value : isProgramDetailsError.value,
  );

  function handleRetryProgramLoading() {
    if (creationMode.value) return refetchInstalledApplianceCategories();

    return refetchProgramDetails();
  }

  async function handleCreateProgram() {
    if (isCreatingProgram.value || !subPrograms.value) return;

    const submittedGeneration = viewGeneration;
    const submittedSiteId = siteId.value;
    const submittedName = programName.value;
    const submittedColor = programColor.value;

    try {
      const response = await createSiteProgram({
        name: submittedName,
        frontDisplayColor: submittedColor,
        siteId: submittedSiteId,
        subPrograms: subPrograms.value,
      });

      if (submittedGeneration !== viewGeneration) return;

      MixpanelService.trackEvent(EMixpanelEvent.PROGRAM_CREATED, {
        name: submittedName,
        color: submittedColor,
      });

      allowDirtyNavigation = true;

      await router.replace({
        name: ERouteName.PROGRAMMING_DETAILS,
        params: { programId: response.id, siteId: submittedSiteId },
      });
    } catch (e) {
      if (submittedGeneration !== viewGeneration) return;

      toastKiliError(e, "control.programming.creation.error");
    } finally {
      allowDirtyNavigation = false;
    }
  }

  async function handleSaveProgram(): Promise<boolean> {
    if (isSavingProgram.value) return false;

    const submittedGeneration = viewGeneration;
    const submittedSiteId = siteId.value;
    const submittedProgramId = programId.value;
    const submittedName = programName.value;
    const submittedColor = programColor.value;
    const saveState: { stage: "rename" | "ranges" } = { stage: "ranges" };

    try {
      const wasSaved = await save({
        updateProgram: (request) => {
          saveState.stage = "rename";
          return updateSiteProgram({
            siteId: submittedSiteId,
            programId: submittedProgramId,
            request,
          });
        },
        saveSubPrograms: (subPrograms) => {
          saveState.stage = "ranges";
          return updateSubprograms({
            siteId: submittedSiteId,
            programId: submittedProgramId,
            subPrograms,
          });
        },
      });

      if (submittedGeneration !== viewGeneration) return false;
      if (!wasSaved) return false;

      toast.add({
        severity: "success",
        detail: t(k.control.programming.details.edit.success),
      });

      MixpanelService.trackEvent(EMixpanelEvent.PROGRAM_UPDATED, {
        name: submittedName,
        color: submittedColor,
        program_type: programDetails.value?.isSiteDefault ? "default" : "custom",
      });

      if (!isDirty.value) isEditing.value = false;
      return true;
    } catch (e) {
      if (submittedGeneration !== viewGeneration) return false;

      toastKiliError(
        e,
        saveState.stage === "rename" ? "control.programming.renaming.error" : "control.programming.details.edit.error",
      );

      return false;
    }
  }

  function validateProgramName(): boolean {
    if (!isNameValid.value) {
      showNameError.value = true;

      toast.add({
        severity: "error",
        detail: t(k.control.programming.details.identity.programNameRequired),
      });

      return false;
    }

    return true;
  }

  async function handlePrimaryAction() {
    showNameError.value = false;

    if (!creationMode.value) {
      if (!validateProgramName()) return;

      await handleSaveProgram();
      return;
    }

    if (!isLastApplianceCategory.value) {
      validateActiveApplianceCategoryAndSelectNext();
      return;
    }

    if (!validateProgramName()) return;

    validateActiveApplianceCategoryAndSelectNext();

    if (!isConfigurationComplete.value) return;

    await handleCreateProgram();
  }

  async function exitEditor() {
    if (creationMode.value) {
      allowDirtyNavigation = true;
      try {
        await router.push({
          name: ERouteName.PROGRAMMING_LIST,
          params: { siteId: siteId.value },
        });
      } finally {
        allowDirtyNavigation = false;
      }
      return;
    }

    discardChanges();
    isEditing.value = false;
  }

  async function handleCancel() {
    if (isProgramMutationPending.value) return;

    if (isDirty.value) {
      pendingExitAction = exitEditor;
      isUnsavedChangesDialogVisible.value = true;
      return;
    }

    await exitEditor();
  }

  async function handleConfirmUnsavedChanges() {
    const exitAction = pendingExitAction;
    const resolveNavigation = pendingNavigationResolution;

    pendingExitAction = undefined;
    pendingNavigationResolution = undefined;
    isUnsavedChangesDialogVisible.value = false;

    if (resolveNavigation) {
      resolveNavigation(true);
      return;
    }

    await exitAction?.();
  }

  function handleBeforeUnload(event: BeforeUnloadEvent) {
    if (!isDirty.value) return;

    event.preventDefault();
    event.returnValue = "";
  }

  window.addEventListener("beforeunload", handleBeforeUnload);

  onBeforeUnmount(() => {
    window.removeEventListener("beforeunload", handleBeforeUnload);
    pendingNavigationResolution?.(false);
    pendingNavigationResolution = undefined;
  });

  function handleDirtyRouteNavigation() {
    if (allowDirtyNavigation) return;
    if (!isDirty.value) return;
    if (isUnsavedChangesDialogVisible.value) return false;

    return new Promise<boolean>((resolve) => {
      pendingNavigationResolution = resolve;
      isUnsavedChangesDialogVisible.value = true;
    });
  }

  onBeforeRouteLeave(handleDirtyRouteNavigation);
  onBeforeRouteUpdate(handleDirtyRouteNavigation);

  return {
    isProgramSiteDefault,
    breadcrumbFormatter,
    isEditing,
    isUnsavedChangesDialogVisible,
    showNameError,
    siteDetails,
    isSiteDetailsPending,
    isProgramDetailsPending,
    isProgramMutationPending,
    isInstalledApplianceCategoriesPending,
    isInitialized,
    programName,
    programColor,
    subPrograms,
    isNameValid,
    activeApplianceCategory,
    displayedApplianceCategories,
    validatedApplianceCategories,
    activeQuickConfigurations,
    toggleQuickConfiguration,
    handleManualProgramChange,
    primaryActionLabel,
    isPrimaryActionDisabled,
    isPrimaryActionLoading,
    isProgramLoadingError,
    handleRetryProgramLoading,
    handlePrimaryAction,
    handleCancel,
    handleConfirmUnsavedChanges,
    handleSaveProgram,
  };
}
